import { Tabs } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFeatureToggles } from '../../context/featureToggles';
import { Permission } from '../../user/types/Permission';
import { useUser } from '../../user/userContext';
import { userHasPermission } from '../../user/userUtils';
import BookingOrderTypeSelect, {
  BookingOrderDetailsOrderType,
} from '../components/BookingOrderTypeSelect';
import BookingOrderDetails from './BookingOrderDetails';
import OpenClassBookingOrderDetails from './OpenClassBookingOrderDetails';

type OpenClassOrderMode = 'company' | 'standalone';

const NewBookingOrder: React.FC = () => {
  const [currentUser] = useUser();
  const { isOpenClassesEnabled } = useFeatureToggles();
  const [selectedOrderType, setSelectedOrderType] =
    useState<BookingOrderDetailsOrderType>(
      BookingOrderDetailsOrderType.Standard,
    );
  const [openClassOrderMode, setOpenClassOrderMode] =
    useState<OpenClassOrderMode>('company');
  const { t } = useTranslation();
  const canCreateOpenClassOrder =
    isOpenClassesEnabled &&
    userHasPermission(currentUser, Permission.COURSE_INSTANCE_CREATE);

  const orderTypeSelector = canCreateOpenClassOrder ? (
    <BookingOrderTypeSelect
      value={selectedOrderType}
      onChange={setSelectedOrderType}
    />
  ) : null;

  const openClassOrderModeSelector = canCreateOpenClassOrder ? (
    <Tabs
      activeKey={openClassOrderMode}
      className="min-w-72"
      items={[
        {
          key: 'company',
          label: t('views.OpenClassBookingOrderDetails.companyOrder'),
        },
        {
          key: 'standalone',
          label: t('views.OpenClassBookingOrderDetails.standaloneOrder'),
        },
      ]}
      onChange={(key) => setOpenClassOrderMode(key as OpenClassOrderMode)}
    />
  ) : null;

  if (selectedOrderType === BookingOrderDetailsOrderType.OpenClass) {
    const selectors = (
      <>
        {orderTypeSelector}
        {openClassOrderModeSelector}
      </>
    );
    return (
      <OpenClassBookingOrderDetails
        isNewOrder
        isStandalone={openClassOrderMode === 'standalone'}
        // Remount to drop the other mode's form state.
        key={openClassOrderMode}
        orderTypeSelector={selectors}
      />
    );
  }

  return (
    <BookingOrderDetails isNewOrder orderTypeSelector={orderTypeSelector} />
  );
};

export default NewBookingOrder;
